/**
 * Resolve the lazily-loaded page component for the current URL *before* the first
 * React commit.
 *
 * The problem this solves (measured 2026-09-05, before the fix):
 *
 *   The server sends real, readable HTML inside #root. React then mounts, finds
 *   that the route's component is a `lazy()` whose chunk has not arrived, throws
 *   to the nearest <Suspense>, and commits `PageLoader` — a spinner and the word
 *   "Loading..." — over content the reader was already reading. When the chunk
 *   lands, the content comes back. On a cold throttled mobile connection the
 *   homepage went 2,575 characters at 924ms, down to 436 characters from ~2.9s to
 *   ~8.7s, then back to 3,919. On fast desktop the same gap ran about 1.15s.
 *
 * The fix is to warm the chunk first and only then commit. webpack dedupes by
 * module path, so importing the module here and `lazy(() => import(...))` in
 * App.tsx resolve the *same* chunk — by the time React renders, the factory
 * resolves from cache and Suspense has nothing to wait for.
 *
 * Only routes that actually ship SSR content into #root need to be listed. Article
 * pages are already exempt: EnhancedArticlePage is deliberately not lazy (App.tsx),
 * for this same reason. Archive pages ship no SPA bundle at all. Anything not
 * listed here falls through to Promise.resolve() and behaves exactly as before.
 *
 * Deliberately NOT done by static-importing HomePage into main.js: that merges its
 * dependency graph into the bundle every /article visitor downloads, and /article
 * is the highest-traffic template on the site.
 */
export function preloadRouteChunk(pathname: string): Promise<unknown> {
  const p = pathname.replace(/\/+$/, '') || '/';

  if (p === '/') return import('./pages/HomePage');
  if (p === '/news') return import('./pages/NewsHubPage');
  if (p.startsWith('/news/topic/')) return import('./pages/TopicPage');

  return Promise.resolve();
}

/**
 * Never let a slow or failed chunk hold the page hostage: if the import has not
 * settled within `ms`, render anyway and let Suspense do what it did before.
 */
export function preloadWithDeadline(pathname: string, ms = 3000): Promise<unknown> {
  return Promise.race([
    preloadRouteChunk(pathname).catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, ms)),
  ]);
}
